/**
 * ontarioBankingDays.js
 *
 * Which days money can actually leave a bank account in Ontario: not a weekend,
 * and not a day branches are closed.
 *
 * Holiday RULES come from `@utils/canadianHolidays`, the app's one calendar,
 * which derives every date from its rule (third Monday of February, the Friday
 * before Easter) rather than a table that would expire — a 2029 date is as
 * correct as a 2026 one.
 *
 * Two things are layered on top of its Ontario set here, because that module
 * answers an EMPLOYMENT-LAW question and this one is a BANKING question:
 *
 *   1. Remembrance Day and the National Day for Truth and Reconciliation are not
 *      ESA public holidays — Ontario employees are not entitled to them, which is
 *      exactly why the shared module omits them for 'ON'. But branches close and
 *      money does not move. Adding them there would wrongly grant paid stat days
 *      to the vacation and schedule pages that read the same map.
 *   2. Observed days. A fixed-date holiday landing on a weekend closes branches
 *      on the following weekday instead, which employment law tracks separately
 *      from the closure itself.
 *
 * Date helpers are intentionally local: this is a leaf module, and importing them
 * from a caller would make the dependency circular.
 */
import { getHolidayMap } from "@utils/canadianHolidays";

/** "2026-07-24" -> Date at LOCAL midnight (never UTC, which would shift a day). */
function parseISO(s) {
  if (!s) return null;
  const [y, m, d] = String(s).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** Date -> "YYYY-MM-DD", read in local time to match `parseISO`. */
function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function isWeekend(d) {
  const dow = d.getDay();
  return dow === 0 || dow === 6;
}

/**
 * Add the observed weekday for any holiday that falls on a weekend. Keys are
 * walked in order so Christmas claims its Monday before Boxing Day pushes to the
 * Tuesday behind it.
 */
function withObserved(map) {
  const out = { ...map };
  for (const iso of Object.keys(map).sort()) {
    const d = parseISO(iso);
    if (!d || !isWeekend(d)) continue;
    let obs = addDays(d, 1);
    while (isWeekend(obs) || out[toISO(obs)]) obs = addDays(obs, 1);
    out[toISO(obs)] = { name: `${map[iso].name} (observed)` };
  }
  return out;
}

const closureCache = new Map();

function ontarioClosures(year) {
  let map = closureCache.get(year);
  if (!map) {
    map = withObserved({
      ...getHolidayMap([year], "ON"),
      [`${year}-09-30`]: { name: "National Day for Truth and Reconciliation" },
      [`${year}-11-11`]: { name: "Remembrance Day" },
    });
    closureCache.set(year, map);
  }
  return map;
}

/**
 * Why `date` cannot take a payment — "a Saturday", "Canada Day" — or null when
 * the bank is open. An outflow dated to a day money does not move is dated to a
 * day it cannot clear.
 *
 * @param {Date|null} date
 * @returns {string|null}
 */
export function nonBankingReason(date) {
  if (!date) return null;
  if (isWeekend(date)) return date.getDay() === 6 ? "a Saturday" : "a Sunday";
  return ontarioClosures(date.getFullYear())[toISO(date)]?.name ?? null;
}

/** The Ontario bank holiday on `date`, ignoring weekends. Null when there is none. */
export function holidayName(date) {
  if (!date || isWeekend(date)) return null;
  return ontarioClosures(date.getFullYear())[toISO(date)]?.name ?? null;
}

export function isBankingDay(date) {
  return !!date && !nonBankingReason(date);
}

/** The latest banking day on or before `date`. */
export function prevBankingDay(date) {
  let d = new Date(date);
  // The longest real closure run is a Christmas/Boxing Day weekend, so this
  // settles in a handful of steps; the bound only stops a bad input spinning.
  for (let i = 0; i < 30 && !isBankingDay(d); i++) d = addDays(d, -1);
  return d;
}

/**
 * The banking day CLOSEST to `date`, searching outwards in both directions.
 * Ties break earlier — a Wednesday holiday moves to Tuesday, not Thursday — so a
 * payment never drifts later than it has to.
 *
 * In practice this keeps a moved payment inside its own week: a Friday closure
 * falls back to Thursday (1 day) rather than forward to Monday (3 days), and a
 * Monday closure moves forward to Tuesday rather than back to the previous
 * Friday.
 */
export function nearestBankingDay(date) {
  if (!date) return null;
  if (isBankingDay(date)) return date;
  for (let delta = 1; delta <= 15; delta += 1) {
    const back = addDays(date, -delta);
    if (isBankingDay(back)) return back;
    const forward = addDays(date, delta);
    if (isBankingDay(forward)) return forward;
  }
  return date;
}

/**
 * The first banking day on or AFTER `date` — never earlier.
 *
 * Distinct from `nearestBankingDay`, which is a DISPLAY rule (ties break earlier
 * so a payment does not drift later than it has to). This one backs a REAL move:
 * a payment dated to a Saturday or a holiday cannot clear that day, and pulling
 * it earlier would take the money out before it is due. Banks push forward, so
 * this does too.
 */
export function nextBankingDay(date) {
  let d = date ? new Date(date) : null;
  if (!d) return null;
  // A Christmas/Boxing Day weekend is the longest real closure run; the bound
  // only stops a bad input spinning.
  for (let i = 0; i < 30 && !isBankingDay(d); i += 1) d = addDays(d, 1);
  return d;
}
